/**
 * WelcomeWaiver — first-login membership waiver gate (Option A — per athlete).
 *
 * Sachin spec 2026-02-28 (Q&A on per-kid waivers): every athlete on a
 * parent's account gets their OWN named, dated, IP-stamped Participant
 * Agreement record. The parent reviews the waiver text ONCE and then
 * does a quick name-signature for each athlete + (optionally) themselves
 * in one batch — total ~60 seconds for a family of 3.
 *
 * Surfaces signing for:
 *   1. The parent themselves — if `user.waiver` is missing or stale.
 *   2. Each athlete missing a current waiver — read from
 *      `user.athletes[].waiver_signed`.
 *
 * Each signature POSTs separately to the right endpoint
 * (`/me/waiver/sign` for the parent, `/me/athletes/{id}/waiver/sign`
 * for each athlete). After all succeed → "THANK YOU" interstitial →
 * /dashboard. Card-gate is suppressed for this session (sessionStorage
 * flag) so the parent isn't asked for a card on the same visit.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, ShieldCheck, CheckCircle2, FileSignature, Sparkles, User as UserIcon, Baby,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { WAIVER_TEXT, WAIVER_VERSION } from "@/lib/waiver";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REDIRECT_DELAY_MS = 3200;

function isMinor(dobIso) {
    if (!dobIso) return false;
    const dob = new Date(dobIso);
    if (Number.isNaN(dob.getTime())) return false;
    const age = (Date.now() - dob.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
    return age < 18;
}

function fmtDob(dobIso) {
    if (!dobIso) return null;
    try { return new Date(dobIso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return dobIso; }
}

export default function WelcomeWaiver() {
    const { user, loading, refreshUser } = useAuth();
    const navigate = useNavigate();

    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    // Sachin 2026-03-05: "People are having trouble with the waiver.
    // Can you make it so it's just check boxes and no scrolling has to
    // happen? There should be a link to read it though." Scroll gate
    // retired; the full waiver text now lives behind a link, and each
    // athlete-row's checkbox is the acknowledgement gate.
    const scrollRef = useRef(null);

    // Build the list of entities that need a signature this visit.
    // - Parent-self: if the parent's own waiver signature is missing.
    //   Sachin 2026-03-06 (round 2): the popup on the dashboard now gates
    //   on presence of `waiver.signed_at` (any version), so a parent with
    //   a stale-version booking waiver won't be sent here — but if they
    //   navigate here manually we still want to let them RE-sign.
    //   Previously this branch only fired when `waiver_signed_at` was
    //   completely absent, which dead-ended anyone with a booking-time
    //   signature.
    // - Each athlete: if athlete.waiver_signed is false (server-derived).
    const targets = useMemo(() => {
        if (!user) return [];
        const out = [];
        if (!user.self_waiver_signed) {
            out.push({
                kind: "self",
                key: "self",
                label: user.parent_first_name || user.name || "Account holder",
                sub: user.waiver_signed_at
                    ? "Re-acknowledge current terms"
                    : "Account holder",
            });
        }
        (user.athletes || []).forEach((a) => {
            // Presence-based: any signature counts (Sachin 2026-06 — "make
            // sure if it is signed, it doesn't keep showing even if signed").
            // A kid signed under an older booking version must NOT be re-listed.
            if (a.waiver_signed || a.waiver_signed_at) return; // already covered
            out.push({
                kind: "athlete",
                key: a.athlete_id,
                athlete_id: a.athlete_id,
                label: `${a.first_name || ""} ${a.last_name || ""}`.trim() || "Athlete",
                sub: isMinor(a.dob)
                    ? `Minor · DOB ${fmtDob(a.dob) || "—"}`
                    : `Adult athlete · DOB ${fmtDob(a.dob) || "—"}`,
                minor: isMinor(a.dob),
                dob: a.dob,
            });
        });
        return out;
    }, [user]);

    // Per-target form state: just the agree checkbox now.
    // Sachin 2026-03-05: parents with multiple athletes should only
    // type their signature ONCE — the same signed name is stamped on
    // every waiver POST below. Signature lives in `signedName` (top-
    // level state) rather than per-row.
    const [forms, setForms] = useState({});
    const [signedName, setSignedName] = useState("");
    useEffect(() => {
        // Prefill the shared signature with the parent's full name on
        // first render. Also seed a `{agreed:false}` entry per target.
        if (!user) return;
        const fullName = (
            user.parent_first_name
                ? `${user.parent_first_name} ${user.parent_last_name || ""}`.trim()
                : user.name || ""
        );
        setSignedName((prev) => prev || fullName);
        setForms((prev) => {
            const next = { ...prev };
            targets.forEach((t) => {
                if (!next[t.key]) next[t.key] = { agreed: false };
            });
            return next;
        });
    }, [user, targets.length]);

    const setForm = (key, patch) => setForms((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));

    // Sign button unlocks once AT LEAST ONE person has an agreed
    // checkbox AND the shared signature is filled. Sachin 2026-03-05:
    // "even if only one person is checked, they are signed for. Does
    // not have to be both." — we only POST for the checked ones.
    const checkedTargets = targets.filter((t) => (forms[t.key] || {}).agreed);
    const anyAgreed = checkedTargets.length > 0;
    const nameValid = signedName.trim().length > 1;
    const allComplete = anyAgreed && nameValid;

    const submit = async () => {
        if (!allComplete) {
            if (!anyAgreed) toast.error("Check at least one person to sign for.");
            else toast.error("Type your legal name to sign.");
            return;
        }
        setSubmitting(true);
        try {
            const sharedName = signedName.trim();
            for (const t of checkedTargets) {
                if (t.kind === "self") {
                    await axios.post(
                        `${API}/me/waiver/sign`,
                        {
                            signed_name: sharedName,
                            relationship: "self",
                            version: WAIVER_VERSION,
                            text_snapshot: WAIVER_TEXT,
                        },
                        { withCredentials: true },
                    );
                } else {
                    await axios.post(
                        `${API}/me/athletes/${t.athlete_id}/waiver/sign`,
                        {
                            signed_name: sharedName,
                            relationship: t.minor ? "parent" : "self",
                            version: WAIVER_VERSION,
                            text_snapshot: WAIVER_TEXT,
                        },
                        { withCredentials: true },
                    );
                }
            }
            // Suppress the card-on-file gate for this session — Sachin spec.
            try { window.sessionStorage.setItem("skip_card_gate_once", "1"); } catch (_e) { /* noop */ }
            // Sachin 2026-03-06 (round 3): also stamp the permanent
            // onboarding-dismiss flag. Belt-and-suspenders: if the
            // backend `self_waiver_signed` fails to flip for ANY reason
            // (refresh race, cache, etc.), this localStorage flag
            // still suppresses the popup on future loads. It's cleared
            // if we ever release new terms via a versioned key.
            try { window.localStorage.setItem("maxsc_onboarding_dismissed_forever", new Date().toISOString()); } catch (_e) { /* noop */ }
            await refreshUser?.();
            setDone(true);
            setTimeout(() => navigate("/dashboard", { replace: true }), REDIRECT_DELAY_MS);
        } catch (e) {
            toast.error("Couldn't save your signature(s)", {
                description: e?.response?.data?.detail,
            });
            setSubmitting(false);
        }
    };

    if (loading || !user) {
        return (
            <Shell>
                <div className="flex items-center justify-center py-20">
                    <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
                </div>
            </Shell>
        );
    }

    if (done) {
        const firstName = user.parent_first_name || (user.name || "").split(" ")[0] || "there";
        return (
            <Shell>
                <div data-testid="welcome-waiver-success" className="text-center py-16 max-w-lg mx-auto">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-700/15 ring-2 ring-emerald-500/40 mb-7">
                        <CheckCircle2 className="w-11 h-11 text-emerald-400" />
                    </div>
                    <div className="text-[11px] tracking-[0.3em] uppercase text-emerald-300 mb-3 inline-flex items-center gap-2">
                        <Sparkles className="w-3 h-3" /> Waivers signed
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                        THANK YOU,
                        <br />
                        <span className="text-brand-red">{firstName.toUpperCase()}.</span>
                    </h1>
                    <p className="text-zinc-300 mt-5 leading-relaxed text-base">
                        Excited to meet you. Taking you to your dashboard…
                    </p>
                    <Loader2 className="w-5 h-5 text-zinc-500 animate-spin mx-auto mt-7" />
                </div>
            </Shell>
        );
    }

    // Defensive: if nothing needs signing yet we're stuck on this page,
    // bounce back. Can happen if /auth/me hasn't caught up.
    if (targets.length === 0) {
        return (
            <Shell>
                <div className="text-center py-20">
                    <Loader2 className="w-6 h-6 text-brand-red animate-spin mx-auto mb-4" />
                    <p className="text-zinc-300 text-sm">Nothing to sign right now…</p>
                </div>
            </Shell>
        );
    }

    return (
        <Shell>
            <div data-testid="welcome-waiver-page" className="max-w-2xl mx-auto">
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3 inline-flex items-center gap-2">
                    <FileSignature className="w-3 h-3" />
                    First — your participant agreement
                </div>
                <h1 className="font-display text-4xl md:text-5xl tracking-wider text-white leading-[0.95]">
                    SIGN YOUR
                    <br />
                    <span className="text-brand-red">WAIVER{targets.length > 1 ? "S" : ""}.</span>
                </h1>
                <p className="text-zinc-300 mt-4 leading-relaxed">
                    {targets.length === 1
                        ? "Before anything else — please sign the standard Maximum Sports Conditioning Participant Agreement. Same form every athletic facility uses; takes about 30 seconds. It\u2019s required to use the gym, and we re-confirm it once a year."
                        : `Before anything else — please sign the standard MaxSC Participant Agreement for each person on your account. You only read the agreement once, then add a quick signature for each of the ${targets.length} listed below.`}
                </p>

                <div className="mt-7 border border-brand-red/40 bg-brand-red/5 rounded-lg p-4 flex items-start gap-3">
                    <ShieldCheck className="w-5 h-5 text-brand-red flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-zinc-200 leading-relaxed">
                        <strong className="text-white">Required.</strong> A separate signature is
                        legally required for each minor — every athlete&apos;s waiver gets their name,
                        date of birth, and your signature stamped to it.
                    </div>
                </div>

                <div className="mt-6 border border-zinc-800 bg-zinc-950/40 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="text-[12px] text-zinc-200 flex-1">
                        You&apos;re signing the standard <span className="text-white font-semibold">MaxSC Participant Agreement</span>. Same form every athletic facility uses.
                    </div>
                    <a
                        href="/waiver-read"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="welcome-waiver-read-link"
                        className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase border border-brand-red text-brand-red hover:bg-brand-red hover:text-white transition-colors px-3 py-2 rounded"
                    >
                        Read the full waiver
                    </a>
                </div>

                {/* Hidden scroll node kept to preserve the ref in case any
                    other consumer references it, but the scroll gate is
                    fully retired (Sachin 2026-03-05). */}
                <div ref={scrollRef} className="hidden" aria-hidden="true" />

                {/* Per-target agreement checkboxes.
                    Sachin 2026-03-05: "If a parent has multiple athletes,
                    they fill out the info we need, but they only need to
                    sign once." Name field is pulled out below into ONE
                    shared signature that stamps every waiver POST. */}
                <div className="mt-7 space-y-3">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-semibold">
                        {targets.length === 1
                            ? "Acknowledge"
                            : `Acknowledge — you can sign for one, some, or all ${targets.length}`}
                    </div>
                    {targets.map((t) => {
                        const f = forms[t.key] || { agreed: false };
                        const Icon = t.kind === "self" ? UserIcon : Baby;
                        return (
                            <label
                                key={t.key}
                                data-testid={`welcome-waiver-target-${t.key}`}
                                className={`flex items-start gap-3 border rounded-lg p-4 cursor-pointer transition-colors ${
                                    f.agreed
                                        ? "border-emerald-700/50 bg-emerald-900/10"
                                        : "border-zinc-800 bg-zinc-950/40 hover:border-zinc-700"
                                }`}
                            >
                                <input
                                    type="checkbox"
                                    data-testid={`welcome-waiver-agree-${t.key}`}
                                    checked={f.agreed}
                                    onChange={(e) => setForm(t.key, { agreed: e.target.checked })}
                                    className="mt-1 flex-shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Icon className={`w-4 h-4 ${t.kind === "self" ? "text-brand-red" : "text-amber-400"} flex-shrink-0`} />
                                        <div className="text-sm font-semibold text-white truncate">{t.label}</div>
                                        <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 ml-auto whitespace-nowrap">
                                            {t.sub}
                                        </span>
                                    </div>
                                    <div className="text-[12px] text-zinc-300 leading-snug">
                                        I have read and agree to the Participant Agreement{" "}
                                        {t.kind === "self"
                                            ? "for myself"
                                            : t.minor
                                                ? `as the parent or legal guardian of ${t.label} (DOB ${fmtDob(t.dob) || "—"})`
                                                : `for ${t.label}`}
                                        .
                                    </div>
                                </div>
                            </label>
                        );
                    })}
                </div>

                {/* One signature that stamps every waiver above. */}
                <div className="mt-6 border border-brand-red/30 bg-brand-red/5 rounded-lg p-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red font-semibold mb-2 inline-flex items-center gap-2">
                        <FileSignature className="w-3 h-3" />
                        Your signature
                    </div>
                    <p className="text-[12px] text-zinc-300 mb-3 leading-snug">
                        Type your full legal name below. This signature is stamped on every acknowledgement above — you only sign once.
                    </p>
                    <input
                        type="text"
                        data-testid="welcome-waiver-shared-name"
                        value={signedName}
                        onChange={(e) => setSignedName(e.target.value)}
                        placeholder="Your full legal name"
                        className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-3 text-base text-white focus:outline-none focus:border-brand-red"
                    />
                </div>

                <p className="text-[11px] text-zinc-400 mt-4">
                    Waiver version <span className="text-zinc-200">{WAIVER_VERSION}</span> ·
                    Signed {new Date().toLocaleDateString()} · Annual re-sign required.
                </p>

                <div className="flex justify-end gap-2 mt-7">
                    <button
                        type="button"
                        data-testid="welcome-waiver-submit"
                        onClick={submit}
                        disabled={!allComplete || submitting}
                        className={`inline-flex items-center gap-2 px-6 py-3 text-sm tracking-[0.15em] uppercase transition-colors ${
                            allComplete
                                ? "btn-tactical"
                                : "btn-tactical opacity-60 cursor-not-allowed"
                        }`}
                    >
                        {submitting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <FileSignature className="w-4 h-4" />
                        )}
                        {allComplete
                            ? `Sign & continue${checkedTargets.length < targets.length ? ` (${checkedTargets.length} of ${targets.length})` : ""}`
                            : !anyAgreed
                                ? "Check at least one person"
                                : "Type your signature to finish"}
                    </button>
                </div>
            </div>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen bg-brand-obsidian text-white pt-28 pb-16 px-4 relative overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_85%,rgba(31,45,85,0.45),transparent_55%),radial-gradient(circle_at_85%_15%,rgba(230,0,0,0.10),transparent_55%)] pointer-events-none" />
            <div className="relative">{children}</div>
        </div>
    );
}
